import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Plus, Search, Edit, Trash2, Eye, X, History, Download, Upload, FileSpreadsheet, ChevronDown, Calendar, AlertCircle, CheckCircle, Clock, TrendingUp, TrendingDown, Wallet, Receipt, RefreshCw, CreditCard, DollarSign, Printer, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Subscribers = () => {
  const { user, hasPermission } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isArbe = user?.username?.toLowerCase() === 'arbe' || user?.full_name?.toLowerCase() === 'arbe';
  const canReassignAccounts = isAdmin || isArbe;
  const canEditSubscribers = isAdmin || hasPermission('edit_subscribers');
  const canDeleteSubscribers = isAdmin || hasPermission('delete_subscribers');
  const [subscribers, setSubscribers] = useState([]);
  const [subscriberBalances, setSubscriberBalances] = useState({}); // Store balance info by subscriber ID
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [monthlyHistory, setMonthlyHistory] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyTab, setHistoryTab] = useState('monthly'); // 'monthly' or 'all'
  const [generatingBills, setGeneratingBills] = useState(false);
  const [isQuickPayOpen, setIsQuickPayOpen] = useState(false);
  const [quickPaySubscriber, setQuickPaySubscriber] = useState(null);
  const [unpaidBills, setUnpaidBills] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const receiptRef = useRef(null);
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    address: '',
    plan: '',
    amount: '',
    billing_cycle: 'monthly',
    due_date: '',
    accountNumber: '',
    subscription_start: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchSubscribers();
  }, []);

  // Fetch balance info for all subscribers after loading
  useEffect(() => {
    if (subscribers.length > 0) {
      fetchAllBalances();
    }
  }, [subscribers]);

  const fetchAllBalances = async () => {
    const balances = {};
    // Fetch balances for each subscriber in parallel (limit to 10 concurrent)
    const batchSize = 10;
    for (let i = 0; i < subscribers.length; i += batchSize) {
      const batch = subscribers.slice(i, i + batchSize);
      await Promise.all(
        batch.map(async (sub) => {
          try {
            const response = await axios.get(`${API}/payments/subscriber/${sub.id}/history`);
            balances[sub.id] = {
              total_paid: response.data.summary?.total_paid || 0,
              total_expected: response.data.summary?.total_expected || 0,
              total_owed: response.data.summary?.total_owed || 0,
              months_overdue: response.data.summary?.months_overdue || 0
            };
          } catch (error) {
            console.error(`Failed to fetch balance for ${sub.name}:`, error);
          }
        })
      );
    }
    setSubscriberBalances(balances);
  };

  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/subscribers`);
      setSubscribers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      toast.error('Failed to load subscribers');
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/subscribers/export/excel`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `subscribers_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Error exporting subscribers:', error);
      toast.error('Failed to export subscribers');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/subscribers/template/excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'subscribers_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/subscribers/import/excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import file');
    }
    
    // Reset file input
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return; // Prevent double submission
    
    setSaving(true);
    try {
      if (editingSubscriber) {
        await axios.put(`${API}/subscribers/${editingSubscriber.id}`, formData);
        toast.success('Subscriber updated successfully');
      } else {
        await axios.post(`${API}/subscribers`, formData);
        toast.success('Subscriber added successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchSubscribers();
    } catch (error) {
      console.error('Error saving subscriber:', error);
      toast.error(error.response?.data?.detail || 'Failed to save subscriber');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this subscriber?')) {
      try {
        await axios.delete(`${API}/subscribers/${id}`);
        toast.success('Subscriber deleted successfully');
        fetchSubscribers();
      } catch (error) {
        console.error('Error deleting subscriber:', error);
        toast.error('Failed to delete subscriber');
      }
    }
  };

  const handleEdit = (subscriber) => {
    setEditingSubscriber(subscriber);
    setFormData({
      name: subscriber.name,
      mobile: subscriber.mobile,
      email: subscriber.email || '',
      address: subscriber.address || '',
      plan: subscriber.plan,
      amount: subscriber.amount,
      billing_cycle: subscriber.billing_cycle,
      due_date: subscriber.due_date.split('T')[0],
      accountNumber: subscriber.accountNumber || '',
      subscription_start: subscriber.subscription_start || subscriber.created_at?.split('T')[0] || ''
    });
    setIsDialogOpen(true);
  };

  const handleViewHistory = async (subscriber) => {
    setSelectedSubscriber(subscriber);
    setIsHistoryDialogOpen(true);
    setLoadingHistory(true);
    setHistoryTab('monthly'); // Reset to monthly view
    try {
      // Use the detailed payment history endpoint
      const response = await axios.get(`${API}/payments/subscriber/${subscriber.id}/history`);
      const data = response.data;
      
      // Update subscriber with latest info from API
      setSelectedSubscriber(data.subscriber);
      setPaymentSummary(data.summary);
      setMonthlyHistory(data.monthly_history || []);
      setPaymentHistory(data.all_payments || []);
      setLoadingHistory(false);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      toast.error('Failed to load payment history');
      setLoadingHistory(false);
    }
  };

  const handleGenerateBills = async () => {
    if (!selectedSubscriber) return;
    
    setGeneratingBills(true);
    try {
      const response = await axios.post(`${API}/subscriber-bills/generate/${selectedSubscriber.id}?months=12`);
      toast.success(response.data.message);
      
      // Refresh the payment history to show new bill numbers
      const historyResponse = await axios.get(`${API}/payments/subscriber/${selectedSubscriber.id}/history`);
      const data = historyResponse.data;
      setMonthlyHistory(data.monthly_history || []);
      setPaymentHistory(data.all_payments || []);
    } catch (error) {
      console.error('Error generating bills:', error);
      toast.error('Failed to generate bills');
    } finally {
      setGeneratingBills(false);
    }
  };

  const handleQuickPay = async (subscriber) => {
    setQuickPaySubscriber(subscriber);
    setSelectedBill(null);
    setPaymentAmount('');
    setPaymentMethod('cash');
    setIsQuickPayOpen(true);
    
    try {
      // First, generate bills if not exists
      await axios.post(`${API}/subscriber-bills/generate/${subscriber.id}?months=12`);
      
      // Get unpaid bills
      const response = await axios.get(`${API}/subscriber-bills/${subscriber.id}`);
      const bills = response.data.filter(b => b.status !== 'paid');
      setUnpaidBills(bills);
      
      // Auto-select oldest unpaid bill
      if (bills.length > 0) {
        const oldestUnpaid = bills[bills.length - 1]; // Sorted desc, so last is oldest
        setSelectedBill(oldestUnpaid);
        setPaymentAmount(oldestUnpaid.balance.toString());
      }
    } catch (error) {
      console.error('Error loading unpaid bills:', error);
      toast.error('Failed to load unpaid bills');
    }
  };

  const handleProcessPayment = async () => {
    if (!selectedBill || !paymentAmount) {
      toast.error('Please select a bill and enter amount');
      return;
    }
    
    setProcessingPayment(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.post(`${API}/subscriber-bills/pay`, {
        bill_number: selectedBill.bill_number,
        amount: parseFloat(paymentAmount),
        payment_date: today,
        payment_method: paymentMethod,
        received_by: user?.name || 'Admin',
        notes: `Quick payment for ${selectedBill.billing_month_name}`
      });
      
      toast.success(response.data.message);
      
      // Prepare receipt data
      setReceiptData({
        receiptNumber: `RCP-${Date.now().toString(36).toUpperCase()}`,
        billNumber: selectedBill.bill_number,
        subscriberName: quickPaySubscriber?.name || 'N/A',
        subscriberPlan: quickPaySubscriber?.plan || 'N/A',
        billingMonth: selectedBill?.billing_month_name || 'N/A',
        amount: parseFloat(paymentAmount),
        paymentDate: today,
        paymentMethod: paymentMethod,
        receivedBy: user?.name || 'Admin',
        notes: `Quick payment for ${selectedBill.billing_month_name}`,
        newStatus: response.data?.new_status || 'paid',
        remainingBalance: response.data?.balance || 0
      });
      
      setIsQuickPayOpen(false);
      setShowReceipt(true);
      
      // Refresh balances
      fetchAllBalances();
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handlePrintReceipt = () => {
    const printContent = receiptRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Payment Receipt - ${receiptData?.billNumber || 'N/A'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 400px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 15px; }
            .header h1 { margin: 0; font-size: 20px; }
            .header p { margin: 5px 0 0; color: #666; font-size: 12px; }
            .receipt-number { text-align: center; background: #f5f5f5; padding: 8px; margin-bottom: 15px; font-family: monospace; }
            .row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px dashed #ddd; }
            .row:last-child { border-bottom: none; }
            .label { color: #666; font-size: 12px; }
            .value { font-weight: bold; font-size: 13px; }
            .amount-row { background: #e8f5e9; padding: 10px; margin: 15px 0; border-radius: 5px; }
            .amount-row .value { font-size: 18px; color: #2e7d32; }
            .footer { text-align: center; margin-top: 20px; padding-top: 15px; border-top: 2px solid #333; font-size: 11px; color: #666; }
            .status { display: inline-block; padding: 3px 10px; border-radius: 15px; font-size: 11px; font-weight: bold; }
            .status.paid { background: #c8e6c9; color: #2e7d32; }
            .status.partial { background: #fff3e0; color: #e65100; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handleEmailReceipt = async () => {
    if (!receiptData) return;
    
    // Get subscriber email
    const subscriberEmail = quickPaySubscriber?.email;
    
    if (!subscriberEmail) {
      toast.error('Subscriber does not have an email address on file');
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/email-receipt`, {
        subscriber_email: subscriberEmail,
        receipt_number: receiptData.receiptNumber,
        bill_number: receiptData.billNumber,
        subscriber_name: receiptData.subscriberName,
        subscriber_plan: receiptData.subscriberPlan,
        billing_month: receiptData.billingMonth,
        amount: receiptData.amount,
        payment_date: receiptData.paymentDate,
        payment_method: receiptData.paymentMethod,
        received_by: receiptData.receivedBy,
        status: receiptData.newStatus,
        remaining_balance: receiptData.remainingBalance || 0
      });
      
      toast.success(response.data.message);
      if (response.data.note) {
        toast.info(response.data.note, { duration: 5000 });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email receipt');
    } finally {
      setSendingEmail(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      mobile: '',
      email: '',
      address: '',
      plan: '',
      amount: '',
      billing_cycle: 'monthly',
      due_date: '',
      accountNumber: '',
      subscription_start: new Date().toISOString().split('T')[0]
    });
    setEditingSubscriber(null);
  };

  const filteredSubscribers = subscribers.filter(sub =>
    sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.mobile.includes(searchTerm) ||
    (sub.accountNumber && sub.accountNumber.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'overdue':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'inactive':
        return 'bg-slate-100 text-slate-700 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const handleStatusChange = async (subscriberId, newStatus) => {
    try {
      await axios.put(`${API}/subscribers/${subscriberId}/status`, { status: newStatus });
      toast.success(`Status changed to ${newStatus}`);
      fetchSubscribers();
    } catch (error) {
      console.error('Error changing status:', error);
      toast.error(error.response?.data?.detail || 'Failed to change status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subscribers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex-1 w-full sm:w-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <Input
              type="text"
              placeholder="Search subscribers..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-subscribers"
            />
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canReassignAccounts && (
            <Button 
              variant="outline" 
              onClick={async () => {
                try {
                  const response = await axios.post(`${API}/subscribers/reassign-all-account-numbers`);
                  toast.success(response.data.message);
                  fetchSubscribers();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to reassign account numbers');
                }
              }}
              title="Reassign ALL account numbers (00001, 00002, etc.) sorted by name A-Z"
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              <RefreshCw size={20} className="mr-2" />
              Assign Acct #
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={handleDownloadTemplate}
            title="Download Excel template for importing"
          >
            <FileSpreadsheet size={20} className="mr-2" />
            Template
          </Button>
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            title="Import subscribers from Excel"
          >
            <Upload size={20} className="mr-2" />
            Import
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImportExcel}
            accept=".xlsx,.xls"
            className="hidden"
          />
          <Button 
            variant="outline" 
            onClick={handleExportExcel}
            disabled={exporting || subscribers.length === 0}
            data-testid="export-excel-btn"
          >
            <Download size={20} className="mr-2" />
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-subscriber-btn">
                <Plus size={20} className="mr-2" />
                Add Subscriber
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="subscriber-dialog">
            <DialogHeader>
              <DialogTitle>{editingSubscriber ? 'Edit Subscriber' : 'Add New Subscriber'}</DialogTitle>
              <DialogDescription>
                {editingSubscriber ? 'Update subscriber information below.' : 'Fill in the details to add a new subscriber.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="accountNumber">Account Number</Label>
                  {editingSubscriber ? (
                    <Input
                      id="accountNumber"
                      value={formData.accountNumber}
                      disabled
                      className="bg-slate-100 font-mono"
                      data-testid="subscriber-account-number-input"
                    />
                  ) : (
                    <div className="mt-1 px-3 py-2 bg-slate-100 rounded-md text-sm text-slate-600 border" data-testid="subscriber-account-number-auto">
                      Auto-assigned on creation
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="subscriber-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="mobile">Mobile *</Label>
                  <Input
                    id="mobile"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    placeholder="+639123456789"
                    required
                    data-testid="subscriber-mobile-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="subscriber-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="plan">Plan *</Label>
                  <Input
                    id="plan"
                    value={formData.plan}
                    onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                    placeholder="e.g., 50 Mbps"
                    required
                    data-testid="subscriber-plan-input"
                  />
                </div>
                <div>
                  <Label htmlFor="amount">Amount (PHP) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                    data-testid="subscriber-amount-input"
                  />
                </div>
                <div>
                  <Label htmlFor="billing_cycle">Billing Cycle *</Label>
                  <Select value={formData.billing_cycle} onValueChange={(value) => setFormData({ ...formData, billing_cycle: value })}>
                    <SelectTrigger data-testid="subscriber-billing-cycle-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="due_date">Due Date *</Label>
                  <Input
                    id="due_date"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    required
                    data-testid="subscriber-due-date-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="subscription_start">Subscription Start Date *</Label>
                  <Input
                    id="subscription_start"
                    type="date"
                    value={formData.subscription_start}
                    onChange={(e) => setFormData({ ...formData, subscription_start: e.target.value })}
                    required
                    data-testid="subscriber-subscription-start-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Bills will be generated from this month onwards</p>
                </div>
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    data-testid="subscriber-address-input"
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} data-testid="cancel-btn" disabled={saving}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="submit-subscriber-btn" disabled={saving}>
                  {saving ? 'Saving...' : (editingSubscriber ? 'Update' : 'Add')} {!saving && 'Subscriber'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Subscribers List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="subscribers-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Acct #</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Mobile</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Plan</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Monthly</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Balance Due</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Due Date</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredSubscribers.length > 0 ? (
                filteredSubscribers.map((subscriber) => {
                  const balance = subscriberBalances[subscriber.id];
                  const hasBalance = balance && balance.total_owed > 0;
                  return (
                  <tr key={subscriber.id} className="hover:bg-slate-50 transition-colors" data-testid={`subscriber-row-${subscriber.id}`}>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center justify-center min-w-[40px] px-2 py-1 bg-blue-100 text-blue-800 text-sm font-bold rounded">
                        {subscriber.accountNumber || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{subscriber.name}</p>
                            {hasBalance && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded-full border border-red-200" data-testid={`balance-due-badge-${subscriber.id}`}>
                                DUE
                              </span>
                            )}
                          </div>
                          {subscriber.email && <p className="text-sm text-slate-500">{subscriber.email}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-700 font-mono text-sm">{subscriber.mobile}</td>
                    <td className="px-6 py-4 text-slate-700">{subscriber.plan}</td>
                    <td className="px-6 py-4 font-semibold text-slate-900">₱{subscriber.amount.toLocaleString()}</td>
                    <td className="px-6 py-4">
                      {balance ? (
                        <div>
                          {hasBalance ? (
                            <p className="font-bold text-red-600" data-testid={`balance-amount-${subscriber.id}`}>
                              ₱{balance.total_owed.toLocaleString()}
                            </p>
                          ) : (
                            <p className="font-medium text-green-600">₱0</p>
                          )}
                          {balance.months_overdue > 0 && (
                            <p className="text-xs text-red-500">{balance.months_overdue} month(s) overdue</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">Loading...</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-700">{new Date(subscriber.due_date).toLocaleDateString()}</td>
                    <td className="px-6 py-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button 
                            className={`px-3 py-1 rounded-full text-xs font-medium border cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1 ${getStatusColor(subscriber.status)}`}
                            data-testid={`status-${subscriber.id}`}
                          >
                            {subscriber.status}
                            <ChevronDown size={12} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem 
                            onClick={() => handleStatusChange(subscriber.id, 'active')}
                            className={subscriber.status === 'active' ? 'bg-emerald-50' : ''}
                          >
                            <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2"></span>
                            Active
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleStatusChange(subscriber.id, 'inactive')}
                            className={subscriber.status === 'inactive' ? 'bg-slate-50' : ''}
                          >
                            <span className="w-2 h-2 rounded-full bg-slate-400 mr-2"></span>
                            Inactive
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleStatusChange(subscriber.id, 'overdue')}
                            className={subscriber.status === 'overdue' ? 'bg-red-50' : ''}
                          >
                            <span className="w-2 h-2 rounded-full bg-red-500 mr-2"></span>
                            Overdue
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {hasBalance && (
                          <button
                            onClick={() => handleQuickPay(subscriber)}
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                            title="Quick Pay"
                            data-testid={`quick-pay-${subscriber.id}`}
                          >
                            <CreditCard size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleViewHistory(subscriber)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="View Payment History"
                          data-testid={`view-history-${subscriber.id}`}
                        >
                          <History size={18} />
                        </button>
                        {canEditSubscribers && (
                          <button
                            onClick={() => handleEdit(subscriber)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit subscriber"
                            data-testid={`edit-subscriber-${subscriber.id}`}
                          >
                            <Edit size={18} />
                          </button>
                        )}
                        {canDeleteSubscribers && (
                          <button
                            onClick={() => handleDelete(subscriber.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete subscriber"
                            data-testid={`delete-subscriber-${subscriber.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-slate-500">
                    No subscribers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment History Dialog */}
      <Dialog open={isHistoryDialogOpen} onOpenChange={setIsHistoryDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="payment-history-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>Payment History</DialogTitle>
                <DialogDescription>
                  Detailed payment records for {selectedSubscriber?.name}
                </DialogDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerateBills}
                disabled={generatingBills}
                className="flex items-center gap-2"
                data-testid="generate-bills-btn"
              >
                {generatingBills ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  <Receipt size={16} />
                )}
                {generatingBills ? 'Generating...' : 'Generate Bills'}
              </Button>
            </div>
          </DialogHeader>
          {loadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Subscriber Info Card */}
              {selectedSubscriber && (
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-slate-600">Subscriber</p>
                      <p className="font-semibold text-slate-900">{selectedSubscriber.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Plan</p>
                      <p className="font-semibold text-slate-900">{selectedSubscriber.plan}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Monthly Amount</p>
                      <p className="font-semibold text-slate-900">₱{selectedSubscriber.amount?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Due Date</p>
                      <p className="font-semibold text-slate-900">{selectedSubscriber.due_date || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Subscription Start</p>
                      <p className="font-semibold text-slate-900">{selectedSubscriber.subscription_start ? new Date(selectedSubscriber.subscription_start).toLocaleDateString('en-PH', { month: 'long', year: 'numeric' }) : 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary Cards */}
              {paymentSummary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle size={16} className="text-green-600" />
                      <p className="text-xs text-green-700 font-medium">Total Paid</p>
                    </div>
                    <p className="text-xl font-bold text-green-700">₱{paymentSummary.total_paid?.toLocaleString()}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle size={16} className="text-red-600" />
                      <p className="text-xs text-red-700 font-medium">Total Owed</p>
                    </div>
                    <p className="text-xl font-bold text-red-700">₱{paymentSummary.total_owed?.toLocaleString()}</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock size={16} className="text-amber-600" />
                      <p className="text-xs text-amber-700 font-medium">Months Overdue</p>
                    </div>
                    <p className="text-xl font-bold text-amber-700">{paymentSummary.months_overdue || 0}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Wallet size={16} className="text-blue-600" />
                      <p className="text-xs text-blue-700 font-medium">Payments Made</p>
                    </div>
                    <p className="text-xl font-bold text-blue-700">{paymentSummary.payment_count || 0}</p>
                  </div>
                </div>
              )}

              {/* Tab Buttons */}
              <div className="flex gap-2 border-b border-slate-200 pb-2">
                <button
                  onClick={() => setHistoryTab('monthly')}
                  className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                    historyTab === 'monthly' 
                      ? 'bg-slate-900 text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="monthly-tab-btn"
                >
                  Monthly Breakdown
                </button>
                <button
                  onClick={() => setHistoryTab('all')}
                  className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                    historyTab === 'all' 
                      ? 'bg-slate-900 text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="all-payments-tab-btn"
                >
                  All Payments ({paymentHistory.length})
                </button>
              </div>

              {/* Monthly Breakdown View */}
              {historyTab === 'monthly' && (
                <div className="space-y-2" data-testid="monthly-breakdown-view">
                  {monthlyHistory.length > 0 ? (
                    <>
                      {monthlyHistory.map((month, idx) => (
                        <div
                          key={month.month}
                          className={`p-4 rounded-lg border ${
                            month.status === 'paid' 
                              ? 'bg-green-50 border-green-200' 
                              : month.status === 'partial'
                              ? 'bg-amber-50 border-amber-200'
                              : month.status === 'current'
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-red-50 border-red-200'
                          }`}
                          data-testid={`month-row-${month.month}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                month.status === 'paid' 
                                  ? 'bg-green-200' 
                                  : month.status === 'partial'
                                  ? 'bg-amber-200'
                                  : month.status === 'current'
                                  ? 'bg-blue-200'
                                  : 'bg-red-200'
                              }`}>
                                {month.status === 'paid' ? (
                                  <CheckCircle size={20} className="text-green-700" />
                                ) : month.status === 'partial' ? (
                                  <TrendingUp size={20} className="text-amber-700" />
                                ) : month.status === 'current' ? (
                                  <Calendar size={20} className="text-blue-700" />
                                ) : (
                                  <AlertCircle size={20} className="text-red-700" />
                                )}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-slate-900">{month.month_name}</p>
                                  {month.bill_number && (
                                    <span className="px-2 py-0.5 bg-slate-200 text-slate-700 text-xs font-mono rounded" data-testid={`bill-number-${month.month}`}>
                                      {month.bill_number}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600">
                                  Expected: ₱{month.expected_amount?.toLocaleString()}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-slate-900">
                                Paid: ₱{month.amount_paid?.toLocaleString()}
                              </p>
                              {month.balance > 0 && (
                                <p className="text-sm text-red-600 font-medium">
                                  Balance: ₱{month.balance?.toLocaleString()}
                                </p>
                              )}
                              <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                month.status === 'paid' 
                                  ? 'bg-green-200 text-green-800' 
                                  : month.status === 'partial'
                                  ? 'bg-amber-200 text-amber-800'
                                  : month.status === 'current'
                                  ? 'bg-blue-200 text-blue-800'
                                  : 'bg-red-200 text-red-800'
                              }`}>
                                {month.status === 'current' ? 'Current Month' : month.status.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Calendar size={48} className="mx-auto mb-3 text-slate-300" />
                      <p>No monthly history available</p>
                    </div>
                  )}
                </div>
              )}

              {/* All Payments View */}
              {historyTab === 'all' && (
                <div className="space-y-2" data-testid="all-payments-view">
                  {paymentHistory.length > 0 ? (
                    <>
                      {paymentHistory.map((payment) => (
                        <div
                          key={payment.id}
                          className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200 hover:border-blue-300 transition-colors"
                          data-testid={`history-payment-${payment.id}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                <span className="text-green-600 font-bold">₱</span>
                              </div>
                              <div>
                                <p className="font-semibold text-slate-900">₱{payment.amount?.toLocaleString()}</p>
                                <p className="text-sm text-slate-600">
                                  {new Date(payment.payment_date).toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                  })}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium border border-blue-200">
                              {payment.payment_method}
                            </span>
                            {payment.notes && (
                              <p className="text-xs text-slate-500 mt-1">{payment.notes}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <History size={48} className="mx-auto mb-3 text-slate-300" />
                      <p>No payment records found</p>
                      <p className="text-sm mt-1">This subscriber hasn't made any payments yet</p>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex justify-end">
                <Button onClick={() => setIsHistoryDialogOpen(false)} variant="outline" data-testid="close-history-btn">
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Pay Dialog */}
      <Dialog open={isQuickPayOpen} onOpenChange={setIsQuickPayOpen}>
        <DialogContent className="max-w-md" data-testid="quick-pay-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard size={20} className="text-emerald-600" />
              Quick Pay
            </DialogTitle>
            <DialogDescription>
              Record payment for {quickPaySubscriber?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Subscriber Info */}
            {quickPaySubscriber && (
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <p className="font-semibold text-slate-900">{quickPaySubscriber.name}</p>
                <p className="text-sm text-slate-600">Monthly: ₱{quickPaySubscriber.amount?.toLocaleString()}</p>
              </div>
            )}
            
            {/* Bill Selection */}
            <div className="space-y-2">
              <Label>Select Bill to Pay</Label>
              {unpaidBills.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {unpaidBills.map((bill) => (
                    <div
                      key={bill.id}
                      onClick={() => {
                        setSelectedBill(bill);
                        setPaymentAmount(bill.balance.toString());
                      }}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedBill?.id === bill.id
                          ? 'bg-emerald-50 border-emerald-300'
                          : 'bg-white border-slate-200 hover:border-emerald-200'
                      }`}
                      data-testid={`bill-option-${bill.bill_number}`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-mono text-sm font-semibold text-slate-700">{bill.bill_number}</p>
                          <p className="text-sm text-slate-600">{bill.billing_month_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-red-600">₱{bill.balance?.toLocaleString()}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            bill.status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {bill.status === 'partial' ? 'PARTIAL' : 'UNPAID'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-slate-500">
                  <CheckCircle size={32} className="mx-auto mb-2 text-green-500" />
                  <p>No unpaid bills!</p>
                </div>
              )}
            </div>
            
            {selectedBill && (
              <>
                {/* Payment Amount */}
                <div className="space-y-2">
                  <Label htmlFor="paymentAmount">Payment Amount</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₱</span>
                    <Input
                      id="paymentAmount"
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      className="pl-8"
                      placeholder="Enter amount"
                      data-testid="quick-pay-amount-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    Balance: ₱{selectedBill.balance?.toLocaleString()}
                  </p>
                </div>
                
                {/* Payment Method */}
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger data-testid="quick-pay-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="gcash">GCash</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            
            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setIsQuickPayOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleProcessPayment}
                disabled={!selectedBill || !paymentAmount || processingPayment}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="process-payment-btn"
              >
                {processingPayment ? (
                  <RefreshCw size={16} className="animate-spin mr-2" />
                ) : (
                  <DollarSign size={16} className="mr-2" />
                )}
                {processingPayment ? 'Processing...' : 'Pay Now'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="max-w-md" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt size={20} className="text-green-600" />
              Payment Receipt
            </DialogTitle>
            <DialogDescription>
              Payment recorded successfully
            </DialogDescription>
          </DialogHeader>
          
          {receiptData && (
            <div ref={receiptRef} className="bg-white">
              {/* Receipt Content for Print */}
              <div className="header text-center border-b-2 border-slate-800 pb-3 mb-4">
                <h1 className="text-xl font-bold text-slate-800">PAYMENT RECEIPT</h1>
                <p className="text-sm text-slate-500 mt-1">Mipay Subscription Services</p>
              </div>
              
              <div className="receipt-number text-center bg-slate-100 p-2 rounded font-mono text-sm mb-4">
                {receiptData.receiptNumber}
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between py-1 border-b border-dashed border-slate-200">
                  <span className="text-slate-500 text-sm">Bill Number</span>
                  <span className="font-semibold font-mono">{receiptData.billNumber}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-dashed border-slate-200">
                  <span className="text-slate-500 text-sm">Subscriber</span>
                  <span className="font-semibold">{receiptData.subscriberName}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-dashed border-slate-200">
                  <span className="text-slate-500 text-sm">Plan</span>
                  <span className="font-semibold">{receiptData.subscriberPlan}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-dashed border-slate-200">
                  <span className="text-slate-500 text-sm">Billing Month</span>
                  <span className="font-semibold">{receiptData.billingMonth}</span>
                </div>
                
                <div className="bg-green-50 p-3 rounded-lg my-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 text-sm">Amount Paid</span>
                    <span className="text-2xl font-bold text-green-700">₱{receiptData.amount?.toLocaleString()}</span>
                  </div>
                </div>
                
                <div className="flex justify-between py-1 border-b border-dashed border-slate-200">
                  <span className="text-slate-500 text-sm">Payment Date</span>
                  <span className="font-semibold">{new Date(receiptData.paymentDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-dashed border-slate-200">
                  <span className="text-slate-500 text-sm">Payment Method</span>
                  <span className="font-semibold capitalize">{receiptData.paymentMethod}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-dashed border-slate-200">
                  <span className="text-slate-500 text-sm">Received By</span>
                  <span className="font-semibold">{receiptData.receivedBy}</span>
                </div>
                <div className="flex justify-between py-1 items-center">
                  <span className="text-slate-500 text-sm">Status</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    receiptData.newStatus === 'paid' ? 'bg-green-100 text-green-700' : 
                    receiptData.newStatus === 'partial' ? 'bg-amber-100 text-amber-700' : 
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {receiptData.newStatus?.toUpperCase()}
                  </span>
                </div>
                {receiptData.remainingBalance > 0 && (
                  <div className="flex justify-between py-1 bg-amber-50 px-2 rounded">
                    <span className="text-amber-700 text-sm">Remaining Balance</span>
                    <span className="font-bold text-amber-700">₱{receiptData.remainingBalance?.toLocaleString()}</span>
                  </div>
                )}
              </div>
              
              <div className="footer text-center mt-4 pt-3 border-t-2 border-slate-800 text-xs text-slate-500">
                <p>Thank you for your payment!</p>
                <p className="mt-1">Generated: {new Date().toLocaleString()}</p>
              </div>
            </div>
          )}
          
          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowReceipt(false)}
              className="flex-1"
            >
              Close
            </Button>
            {quickPaySubscriber?.email && (
              <Button
                onClick={handleEmailReceipt}
                disabled={sendingEmail}
                variant="outline"
                className="flex-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                data-testid="email-receipt-btn"
              >
                {sendingEmail ? (
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                ) : (
                  <Mail size={16} className="mr-2" />
                )}
                {sendingEmail ? 'Sending...' : 'Email'}
              </Button>
            )}
            <Button
              onClick={handlePrintReceipt}
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid="print-receipt-btn"
            >
              <Printer size={16} className="mr-2" />
              Print
            </Button>
          </div>
          {!quickPaySubscriber?.email && (
            <p className="text-xs text-slate-500 text-center mt-2">
              No email on file for this subscriber
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Subscribers;